
import React, { useState } from 'react';
import api from '../services/api';
import { X, Users, Globe, Lock, Info, Save, Edit, AlertCircle } from 'lucide-react';
import { Toast } from '../Toast';

interface EditGroupModalProps {
    group: any; // Using any for simplicity
    onClose: () => void;
    onSuccess: () => void;
}

export const EditGroupModal: React.FC<EditGroupModalProps> = ({ group, onClose, onSuccess }) => {
    const [title, setTitle] = useState(group.title || '');
    const [description, setDescription] = useState(group.description || '');
    const [isPublic, setIsPublic] = useState(group.is_public || false);
    const [username, setUsername] = useState(group.username || '');
    const [rulesText, setRulesText] = useState(group.rules_text || '');
    const [autoApprove, setAutoApprove] = useState(group.auto_approve || false);

    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);

    const handleSave = async () => {
        if (!title) {
            setToast({ msg: "O título não pode ser vazio.", type: 'error' });
            return;
        }

        if (isPublic && !username) {
            setToast({ msg: "Grupos públicos precisam de um Link Público (username).", type: 'error' });
            return;
        }

        setLoading(true);
        try {
            await api.put(`/groups/${group.id}`, {
                title: title,
                description: description,
                is_public: isPublic,
                username: isPublic ? username.replace('t.me/', '').replace('@', '') : null,
                rules_text: rulesText,
                auto_approve: autoApprove
            });

            onSuccess();
            onClose();
        } catch (e: any) {
            console.error(e);
            const errorMsg = e.response?.data?.detail || "Erro ao editar grupo.";
            setToast({ msg: errorMsg, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, backdropFilter: 'blur(8px)'
        }}>
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="modal-content-premium" style={{
                background: '#161618',
                borderRadius: '24px',
                width: '550px',
                maxWidth: '95%',
                border: '1px solid #333',
                color: 'white',
                boxShadow: '0 40px 80px rgba(0, 0, 0, 0.7)',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    padding: '24px 32px',
                    borderBottom: '1px solid #282828',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#1a1a1c'
                }}>
                    <div>
                        <h2 style={{ fontSize: '20px', fontWeight: 600, margin: 0, letterSpacing: '-0.5px' }}>
                            Editar Grupo
                        </h2>
                        <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                            Informações e visibilidade
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: '#222', border: 'none', color: '#fff',
                        cursor: 'pointer', padding: '8px', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: '0.2s'
                    }}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '24px', overflowY: 'auto', maxHeight: '60vh' }}>

                    {/* Título */}
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#888', marginBottom: '8px', letterSpacing: '1px' }}>TÍTULO DO GRUPO</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                style={{
                                    width: '100%', background: '#0a0a0a', border: '1px solid #333', borderRadius: '12px',
                                    padding: '16px 16px 16px 48px', color: '#fff', fontSize: '15px', outline: 'none',
                                    transition: 'border-color 0.2s', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#ff6a00'}
                                onBlur={(e) => e.target.style.borderColor = '#333'}
                                placeholder="Nome do grupo"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                            />
                            <Edit size={20} color="#ff6a00" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                        </div>
                    </div>

                    {/* Descrição */}
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#888', marginBottom: '8px', letterSpacing: '1px' }}>DESCRIÇÃO</label>
                        <textarea
                            style={{
                                width: '100%', background: '#0a0a0a', border: '1px solid #333', borderRadius: '12px',
                                padding: '16px', color: '#fff', fontSize: '14px', minHeight: '80px',
                                outline: 'none', resize: 'vertical', fontFamily: 'inherit',
                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)', transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#ff6a00'}
                            onBlur={(e) => e.target.style.borderColor = '#333'}
                            placeholder="Sobre o que é este grupo..."
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                        />
                    </div>

                    {/* Regras do Grupo */}
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#888', marginBottom: '8px', letterSpacing: '1px' }}>REGRAS / TERMOS DO GRUPO</label>
                        <textarea
                            style={{
                                width: '100%', background: '#0a0a0a', border: '1px solid #333', borderRadius: '12px',
                                padding: '16px', color: '#fff', fontSize: '14px', minHeight: '100px',
                                outline: 'none', resize: 'vertical', fontFamily: 'inherit',
                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)', transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#ff6a00'}
                            onBlur={(e) => e.target.style.borderColor = '#333'}
                            placeholder="Defina as regras ou termos que o usuário deve aceitar..."
                            value={rulesText}
                            onChange={e => setRulesText(e.target.value)}
                        />
                    </div>

                    {/* Aprovação Automática */}
                    <div style={{
                        background: 'rgba(0, 229, 255, 0.05)', padding: '20px', borderRadius: '16px',
                        border: '1px solid rgba(0, 229, 255, 0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ff6a00', fontSize: '14px', fontWeight: 600 }}>
                                <AlertCircle size={16} /> Aprovação Automática
                            </div>
                            <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 0 0' }}>
                                Aprovar solicitações de entrada se o usuário tiver plano ativo.
                            </p>
                        </div>
                        <div
                            onClick={() => setAutoApprove(!autoApprove)}
                            style={{
                                width: '50px', height: '26px', borderRadius: '13px',
                                background: autoApprove ? '#ff6a00' : '#333',
                                position: 'relative', cursor: 'pointer', transition: '0.3s',
                                border: '1px solid #444'
                            }}
                        >
                            <div style={{
                                width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                                position: 'absolute', top: '2px', left: autoApprove ? '26px' : '2px',
                                transition: '0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                        </div>
                    </div>

                </div>

                {/* Footer */}
                <div style={{
                    padding: '24px 32px',
                    borderTop: '1px solid #282828',
                    background: '#1a1a1c',
                    display: 'flex', justifyContent: 'flex-end', gap: '16px'
                }}>
                    <button onClick={onClose} style={{
                        background: 'transparent', border: '1px solid #444', color: '#fff',
                        padding: '12px 24px', borderRadius: '10px', cursor: 'pointer', fontWeight: 500,
                        fontSize: '14px', transition: '0.2s'
                    }}>
                        Cancelar
                    </button>
                    <button onClick={handleSave} disabled={loading} style={{
                        background: 'var(--btn-gradient)',
                        border: 'none', color: '#000',
                        padding: '12px 32px', borderRadius: '10px', cursor: 'pointer', fontWeight: 700,
                        fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
                        boxShadow: 'none',
                        opacity: loading ? 0.7 : 1
                    }}>
                        {loading ? 'Processando...' : <><Save size={18} /> Salvar Alterações</>}
                    </button>
                </div>
            </div>
        </div>
    );
};
